<script lang="ts">
  import { getAppConfig } from '../../../../../common/config/app-config';
  import { selectCorrectSmartPrice } from '../../../../../common/joodUtils';
  import {
    formatNumber,
    getJoodExpiredMembershipType,
    getJoodMembershipType,
    getStr,
    globalCurrencyTag,
    isDirectionRTL,
    isMobile,
    roundToNDigits,
    type JoodMembershipType,
  } from '../../../../../common/util';
  import Icon from '../../../../common/components/Icon.svelte';
  import { getJoodCardImageUrlNew } from '../../../../product-details/services/pdp-data-services';
  import { pdpSellerInfoData } from '../../../../product-details/store/pdp_store';
  import JoodDayBannerNew from '../../../../product-listing-search/components/JoodDayBannerNew.svelte';
  import type { ProductTilePriceGroupVariantType } from '../product-tile-main';
  import JoodExpire from './JoodExpire.svelte';
  import JoodSavingsNew from './JoodSavingsNew.svelte';

  export let algoliaProductHit;
  export let priceType: JoodMembershipType = 'NOPRIME';
  export let variant: ProductTilePriceGroupVariantType;
  export let isMegasaleProduct = [];
  export const backgoundOverride: JoodMembershipType = null;
  export let smartPrimePrice = null;
  const {
    encodedContextPath,
    countryCode,
    unbxdSearchConfig,
    lang,
    joodExpiredFlag,
    joodFullCappingLimitReached,
    pageId,
    pageTemplate,
    isUserSmartFirstEligible,
  } = getAppConfig();
  let userJoodMembershipType: JoodMembershipType = getJoodMembershipType();
  const expiredJoodMembershipType = getJoodExpiredMembershipType();
  const joodSubscribeLinkHref = `${encodedContextPath}/jood/benefits`;
  const isResponsive = isMobile();
  const discountDecimals = +unbxdSearchConfig.decimalCount;
  const isRTL = isDirectionRTL();
  let bannerType: JoodMembershipType | null = null;
  const {
    tacticalBasicPromo,
    tacticalBasicPrimePrice,
    tacticalVipPromo,
    tacticalVipPrimePrice,
    tacticalSmartFirstPromo,
    tacticalSmartRepeatingPromo,
  } = algoliaProductHit;

  let smartTacticalPromoFlag;
  if (isUserSmartFirstEligible) {
    smartTacticalPromoFlag = tacticalSmartFirstPromo;
  } else {
    smartTacticalPromoFlag = tacticalSmartRepeatingPromo;
  }

  let hasTacticalPromo = false;
  let jooddayBannerType: JoodMembershipType = 'VIPPRIME';
  if (bannerType === 'BASICPRIME' || joodExpiredFlag === 'BASIC') {
    jooddayBannerType = 'BASICPRIME';
    hasTacticalPromo = tacticalBasicPromo && tacticalBasicPrimePrice > 0;
  } else if (bannerType === 'SMARTPRIME' || joodExpiredFlag === 'SMART') {
    jooddayBannerType = 'SMARTPRIME';
    hasTacticalPromo = smartTacticalPromoFlag;
  } else if (bannerType === 'VIPPRIME' || joodExpiredFlag === 'VIP' || userJoodMembershipType === 'NOPRIME') {
    hasTacticalPromo = tacticalVipPromo && tacticalVipPrimePrice > 0;
  }

  if (expiredJoodMembershipType && priceType === expiredJoodMembershipType) {
    userJoodMembershipType = priceType = expiredJoodMembershipType;
  }

  $: subscribeLinkVisible = getSubscribeLinkVisibility(priceType, userJoodMembershipType);
  $: subscribeLinkText = getSubscribeLinkText(priceType, userJoodMembershipType, variant);
  $: displayPrice = getPrice(priceType, algoliaProductHit);
  $: wasPrice = algoliaProductHit.wasPrice || algoliaProductHit?.price?.value;
  $: savedPrice = wasPrice - displayPrice;
  $: hasSavings = getHasSavings(priceType, userJoodMembershipType);
  $: savedPercent = hasSavings && roundToNDigits((savedPrice * 100) / wasPrice, discountDecimals);
  $: savedPercentTagOnTopRight = shouldPercentTagBeOnTopRight(priceType, userJoodMembershipType, algoliaProductHit);
  $: savedPercentVisible = getSavedPercentVisibility(algoliaProductHit);
  $: megaSaleClass = priceType === 'NOPRIME' && isMegasaleProduct?.length ? 'megasale-theme' : '';
  $: pdpJoodImgUrl = getJoodCardImageUrlNew(priceType, true, variant === 'pdp' || 'pdp-popup' ? 90 : 48);
  $: bottomDisclaimerVisible = getBottomDisclaimerVisibility(priceType, userJoodMembershipType, variant);
  $: savedAmountVisible = getSavedAmountVisibility(variant);
  const isPrimeUser = userJoodMembershipType !== 'NOPRIME';

  const restrictedPageIds = ['homepage', 'search', 'productList'];
  const restrictedPageTemplates = ['extraNewCategoryPageTemplate', 'ExtraBrandPageTemplate'];
  const isRestrictedPage = restrictedPageIds.includes(pageId) || restrictedPageTemplates.includes(pageTemplate);

  const showJoodDayBanner = hasTacticalPromo && (isPrimeUser || !isRestrictedPage);

  const showSavings = !showJoodDayBanner;

  // Utility Functions
  $: componentVisible = getComponentVisibility(displayPrice, priceType, joodFullCappingLimitReached);

  function getComponentVisibility(displayPrice, priceType, joodFullCappingLimitReached) {
    /**
     * Rules:
     * 1. If full capping is reached, show only NOPRIME price.
     * 2. If full capping is not reached, show all prices.
     */
    return (
      (joodFullCappingLimitReached !== 'true' && displayPrice > 0) ||
      (joodFullCappingLimitReached === 'true' && priceType === 'NOPRIME')
    );
  }

  function getHasSavings(priceType: JoodMembershipType, userJoodMembershipType: JoodMembershipType) {
    let visibility = false;
    if (
      userJoodMembershipType !== 'BASICPRIME' &&
      userJoodMembershipType !== 'VIPPRIME' &&
      userJoodMembershipType !== 'SMARTPRIME'
    ) {
      visibility = savedPrice > 0 && priceType === 'NOPRIME';
    } else {
      if (joodExpiredFlag === 'BASIC' || joodExpiredFlag === 'VIP' || joodExpiredFlag === 'SMART') {
        visibility = savedPrice > 0 && priceType === 'NOPRIME';
      } else {
        visibility = savedPrice > 0;
      }
    }
    return visibility;
  }

  function getBottomDisclaimerVisibility(
    priceType: JoodMembershipType,
    userJoodMembershipType: JoodMembershipType,
    variant: ProductTilePriceGroupVariantType
  ) {
    /*
            Rules:
            1. Only visible for restock-popup variant
            2. Only visible on gold price for non-prime user
        */
    const visible = variant === 'restock-popup' && priceType === 'VIPPRIME' && userJoodMembershipType === 'NOPRIME';
    return visible;
  }

  function getPrice(priceType: JoodMembershipType, algoliaProductHit: any) {
    let sourcePrice = 0;

    switch (priceType) {
      case 'NOPRIME':
        if (typeof algoliaProductHit?.price === 'number') {
          sourcePrice = algoliaProductHit.price;
        } else if (typeof algoliaProductHit?.price?.value === 'number') {
          sourcePrice = algoliaProductHit.price.value;
        } else if (typeof algoliaProductHit?.price?.formattedValue === 'number') {
          sourcePrice = algoliaProductHit.price.formattedValue;
        } else if (typeof algoliaProductHit?.price === 'string') {
          const numericValue = Number(algoliaProductHit.price.replace(/[^0-9.-]/g, ''));
          sourcePrice = Number.isFinite(numericValue) ? numericValue : 0;
        }
        break;
      case 'BASICPRIME':
        sourcePrice = Number(
          algoliaProductHit?.basicPrimePrice ?? algoliaProductHit?.productBasicPrimeData?.primePrice?.value ?? 0
        );
        break;
      case 'VIPPRIME':
        sourcePrice = Number(
          algoliaProductHit?.vipPrimePrice ?? algoliaProductHit?.productVipPrimeData?.primePrice?.value ?? 0
        );
        break;
      case 'SMARTPRIME':
        sourcePrice = Number(
          selectCorrectSmartPrice(
            algoliaProductHit?.smartFirstPrimePrice,
            algoliaProductHit?.smartRepeatingPrimePrice
          ) ?? 0
        );
        break;
    }

    return Number.isFinite(sourcePrice) ? sourcePrice : 0;
  }
  function getSubscribeLinkVisibility(
    priceType: JoodMembershipType,
    userJoodMembershipType: JoodMembershipType
  ): boolean {
    /*
            Rules:
            1. if gray member, subscribe link on gold price
            2. if blue member, subscribe link on gold price
            3. if gold member, no subscribe link
        */

    let visibility = false;
    if (userJoodMembershipType === 'NOPRIME' || userJoodMembershipType === 'BASICPRIME') {
      visibility = priceType === 'VIPPRIME'; // rule 1 & 2
    } else {
      visibility = false; // rule 3
    }

    return visibility;
  }
  function getSubscribeLinkText(
    priceType: JoodMembershipType,
    userJoodMembershipType: JoodMembershipType,
    variant: ProductTilePriceGroupVariantType
  ): string {
    /*
            Rules:
            1. if gray member, subscribe text on gold price
            2. if blue member, upgrade text on gold price
            3. if gold member, no subscribe text
        */
    let text: string = '';
    if (variant === 'restock-popup') {
      text = getStr('primer.subscribe.button.label');
    } else if (userJoodMembershipType === 'NOPRIME' && priceType === 'VIPPRIME') {
      text = getStr('extra.prime.become.jood.member.start.saving.text'); // rule 1
    } else if (userJoodMembershipType === 'BASICPRIME' && priceType === 'BASICPRIME') {
      text = getStr('extra.jood.gold.upgrade.message'); // rule 2
    }

    return text;
  }
  function shouldPercentTagBeOnTopRight(
    priceType: JoodMembershipType,
    userJoodMembershipType: JoodMembershipType,
    algoliaHit
  ): boolean {
    /*
            Rules:
            1. if no gold / blue price, show on top-right in gray price
            2. if blue member, show on top-right in gold price
            3. if gold member, don't show
            4. if product-tile variant, show on top-right
            5. if plp-list responsive, show on top-right
            6. if plp-grid, show on top-right
        */
    const hasBluePrice = algoliaHit?.basicPrimePrice > 0;
    const hasGoldPrice = algoliaHit?.vipPrimePrice > 0;

    if (variant === 'product-tile') return true;
    if (variant === 'pdp' && priceType === 'NOPRIME') return true;
    if (isResponsive && variant === 'plp-list') return true;

    let showOnTopRight = false; // rule 3
    if ($pdpSellerInfoData?.marketPlaceProduct) {
      showOnTopRight = false;
    } else if (!hasBluePrice && !hasGoldPrice && priceType === 'NOPRIME') {
      showOnTopRight = true; // rule 1
    } else if (userJoodMembershipType === 'BASICPRIME' && priceType === 'VIPPRIME') {
      showOnTopRight = true; // rule 2
    } else if (variant === 'plp-grid' && userJoodMembershipType !== 'BASICPRIME' && priceType !== 'VIPPRIME') {
      showOnTopRight = true;
    }

    return showOnTopRight;
  }
  function getSavedPercentVisibility(algoliaProductHit) {
    return countryCode !== 'OM' || algoliaProductHit?.priceValueDiscountPercentageFlag;
  }

  const getSavedAmountVisibility = (variant: ProductTilePriceGroupVariantType) => {
    /*
        Rules:
        1. Only show saved amount on plp-list and product-tile variants
        2. For plp-list variant, only show saved amount for NOPRIME price
        3. For product-tile variant, show saved amount for both NOPRIME and PRIME prices
    */
    let visibility = true;
    if (isResponsive && (variant === 'plp-list' || variant === 'plp-grid')) {
      visibility = false;
    }
    return visibility;
  };

  $: displayPriceFormat = `<strong>${formatNumber(displayPrice)}</strong>`;
</script>

{#if componentVisible && variant !== 'qac'}
  <section
    class="{megaSaleClass} product-price-new-container jood-{priceType} user-{userJoodMembershipType} expired-{joodExpiredFlag} variant-{variant}"
    class:rtl={isRTL}
    class:c_expireddetails={joodExpiredFlag}
    class:discount-applied-section={savedPercent > 0}
    class:variant-pdp={variant === 'pdp-popup'}
  >
    {#if (variant !== 'pdp-popup' && joodExpiredFlag) || !joodExpiredFlag}
      <section class="middle">
        <section class="middle-left">
          <!-- {#if titleVisible}
          <h4>{title}</h4>
        {/if} -->
          <section class="price">
            {@html getStr('jood.price.with.currency', [globalCurrencyTag, displayPriceFormat])}
            <span class="vat">{getStr('extra.addtocart.inc.of.vat')}</span>

            {#if hasSavings}
              <div class="savings-container">
                <section class="savings">
                  <span class="striked-off">{formatNumber(wasPrice)}</span>
                  {#if savedAmountVisible}
                    <strong class="orange-text">
                      {@html getStr('extra.cart.saved.amt', [globalCurrencyTag, formatNumber(savedPrice)])}
                    </strong>
                  {/if}
                </section>
              </div>
            {/if}
          </section>
          {#if hasSavings && savedPercentVisible}
            <section class="save-percent-tag save-percentage-mweb">
              {getStr('product.percentageDiscount.off', [savedPercent])}
            </section>
            <section class="save-percent-tag save-percentage-web" class:top-right-tag={savedPercentTagOnTopRight}>
              {getStr('product.percentageDiscount.off', [savedPercent])}
            </section>
          {/if}
        </section>

        {#if priceType !== 'NOPRIME' && !joodExpiredFlag}
          <section class="middle-right">
            <img src={pdpJoodImgUrl} alt="Jood Card" />
          </section>
        {/if}
      </section>
    {/if}

    {#if joodExpiredFlag || userJoodMembershipType}
      {#if showJoodDayBanner}
        <JoodDayBannerNew {variant} bannerType={jooddayBannerType} {algoliaProductHit} />
      {:else if showSavings}
        <JoodSavingsNew
          {priceType}
          displayPrice={joodExpiredFlag === 'SMART'
            ? smartPrimePrice
            : joodExpiredFlag === 'BASIC'
              ? algoliaProductHit?.basicPrimePrice
              : joodExpiredFlag === 'VIP'
                ? algoliaProductHit?.vipPrimePrice
                : variant === 'pdp' && userJoodMembershipType === 'NOPRIME'
                  ? algoliaProductHit?.vipPrimePrice
                  : 0}
          isProductCard={true}
        />
      {/if}
    {/if}

    {#if priceType !== 'NOPRIME' && variant !== 'pdp-popup' && !showSavings}
      <JoodExpire place="price-expire-card" />
    {/if}

    {#if subscribeLinkVisible && !joodExpiredFlag}
      <div class="subscribe-upgrade-bottom">
        <a class="subscribe-upgrade-link bottom-link" on:click|stopPropagation href={joodSubscribeLinkHref}>
          {subscribeLinkText}
          <Icon type="font" glyph={lang === 'en' ? 'chevron_right' : 'chevron_left'} mobileSize={12} />
        </a>
      </div>
    {/if}

    {#if bottomDisclaimerVisible}
      <section class="bottom">
        {getStr('extra.pdp.prime.gold.members.disclaimer.text')}
      </section>
    {/if}
  </section>
{/if}

<style lang="scss">
  @import '../../../../common/styles/colors.new.scss';

  .product-price-new-container {
    // border-radius: 12px;
    position: relative;
    overflow: hidden;
    flex: 1 1 calc(50% - 8px);
    box-sizing: border-box;
    letter-spacing: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 2px;

    .left {
      img {
        max-width: 35px;
        object-fit: contain;
        border-radius: 4px;
      }
    }
    .middle {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      // padding: 12px 8px;
      color: $neutral-dark-01;

      .middle-left {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
        flex-grow: 1;
        position: relative;
        gap: 8px;
        .price-alert-mp {
          position: absolute;
          right: 0;
          top: 0;
        }
      }
      .middle-right {
        display: flex;
        justify-content: end;
        align-items: center;
        text-align: end;
        img {
          box-shadow: 0px 4px 4px 0px #00000040;
          max-width: 82px;
          border-radius: 6px;
          max-height: 58px;
        }
      }
    }
    .right {
      align-self: center;
      padding-right: 20px;
    }
    .bottom {
      flex-basis: 100%;
      padding: 12px;
      background: $primary-white;
      margin: -8px;
      margin-top: 0;
      flex-grow: 1;
      border-top: 1px solid $secondary-gray-02;
      color: $neutral-dark-02;
      font-size: 11px;
      line-height: 12px;
      font-weight: 400;
    }
    h4 {
      font-size: 14px;
      line-height: 16px;
      margin: 0;
    }
    .price {
      font-weight: 700;
      font-size: 18px;
      line-height: 18px;
      letter-spacing: 0px;

      .vat {
        font-size: 10px;
        font-weight: 400;
        line-height: 14px;
      }
    }
    .savings-container {
      display: flex;
      gap: 6px;
      margin-top: 4px;
      flex-direction: column;
    }
    .savings {
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .striked-off {
      font-weight: 400;
      font-size: 12px;
      line-height: 14px;
      text-decoration: line-through;
      color: $color-grey;
    }
    .orange-text {
      color: $functional-orange;
      font-size: 12px;
      line-height: 14px;
      @include responsive {
        font-size: 11px;
        line-height: 18px;
      }
    }
    .red-text {
      color: $alert-danger;
      font-size: 12px;
      line-height: 14px;
      @include responsive {
        font-size: 11px;
        line-height: 18px;
      }
    }
    .save-percent-tag {
      display: inline-block;
      padding: 3px 4px;
      border-radius: 3px;
      color: $primary-white;
      font-weight: 700;
      font-size: 10px;
      line-height: 12px;
      background-color: $functional-orange;
      margin-top: 4px;

      @include responsive {
        font-size: 9px;
        line-height: 10px;
        padding: 2px 5px;
      }
      &.top-right-tag {
        position: absolute;
        right: 0px;
        margin-top: 0;
        top: 35%;
        transform: translateY(-50%);
      }
      &.save-percentage-mweb {
        display: none;
      }
    }

    .subscribe-upgrade-bottom {
      padding: 4px 8px;
      background: $primary-white;
      text-align: center;

      .subscribe-upgrade-link {
        display: flex;
        justify-content: center;
        font-weight: 700;
        color: $jood-blue-text;
        font-size: 12px;
        line-height: 14px;
        gap: 2px;
      }
    }

    &.jood-BASICPRIME {
      // border: 1px solid $jood-blue-border;
      // background-color: $jood-blue-light;
      .middle {
        // color: $jood-blue-text;
        .middle-left {
          h4 {
            font-weight: 500;
          }
          .save-percent-tag {
            position: relative;
            right: 0px;
            top: 0px;
            margin-top: 4px;
            margin-bottom: 4px;
            background-color: $functional-orange;
            width: fit-content;
          }
        }
      }
    }
    &.jood-VIPPRIME {
      // border: 1px solid $jood-gold-border;
      // background-color: $jood-gold-light;
      .middle {
        // color: $jood-gold-border;
        .middle-left {
          h4 {
            font-weight: 500;
          }
          .save-percent-tag {
            position: relative;
            right: 0px;
            top: 0px;
            margin-top: 4px;
            margin-bottom: 4px;
            background-color: $functional-orange;
            width: fit-content;
          }
        }
      }
    }
    &.jood-SMARTPRIME {
      .middle {
        color: $neutral-dark-01;
        .middle-left {
          h4 {
            font-weight: 500;
          }
          .save-percent-tag {
            position: relative;
            right: 0px;
            top: 0px;
            margin-top: 4px;
            margin-bottom: 4px;
            background-color: $functional-orange;
            width: fit-content;
          }
        }
      }
    }
    &.user-NOPRIME,
    &.expired-VIP,
    &.expired-BASIC {
      .middle {
        // padding: 8px;
        .middle-right {
          img {
            border-radius: 6px;
            max-width: 63.8px;
            max-height: 45.6px;
            box-shadow: 0px 0px 8px 0px #00000033;
          }
        }
      }
    }

    &.variant-pdp {
      &.user-NOPRIME,
      &.user-VIPPRIME.expired-VIP,
      &.user-BASICPRIME.expired-BASIC {
        .middle {
          height: 100%;
          width: 100%;
          // padding: 16px 8px;
          .middle-left {
            flex-basis: 65%;
            @include responsive {
              flex-basis: 60%;
            }
            h4 {
              font-size: 16px;
              line-height: 18px;
              @include responsive {
                font-size: 12px;
              }
            }
            .price {
              font-size: 30px;
              line-height: 36px;
              margin-top: 4px;
              @include responsive {
                font-size: 20px;
                line-height: 20px;
              }
              .vat {
                font-size: 9px;
                @include responsive {
                  font-size: 6px;
                }
              }
              .savings {
                .striked-off {
                  @include responsive {
                    font-size: 10px;
                    line-height: 12px;
                  }
                }
                .save-percent-tag {
                  @include responsive {
                    font-size: 10px;
                    line-height: 18px;
                    padding: 2px 4px;
                  }
                }
              }
            }
          }
          .middle-right {
            flex-basis: 35%;
            img {
              max-width: 77.7px;
              max-height: 57.4px;
              border-radius: 8px;
              @include responsive {
                max-width: 52px;
                max-height: 37px;
                border-radius: 4px;
              }
            }
          }
        }
        .subscribe-upgrade-bottom {
          padding: 4px;
          @include responsive {
            padding: 2px;
          }
          .subscribe-upgrade-link {
            font-size: 12px;
            @include responsive {
              font-size: 9px;
              gap: 0px;
            }
          }
        }
      }

      &.user-VIPPRIME,
      &.user-BASICPRIME {
        .middle {
          // padding: 16px 14px;
          .middle-left {
            h4 {
              font-size: 18px;
              @include responsive {
                font-size: 16px;
              }
            }
            .price {
              margin-top: 4px;
              font-size: 28px;
              line-height: 32px;
              @include responsive {
                font-size: 24px;
              }
            }
            .vat {
              font-size: 12px;
              @include responsive {
                line-height: 12px;
              }
            }
            .striked-off {
              font-size: 16px;
              line-height: 18px;
              @include responsive {
                font-size: 12px;
                line-height: 12px;
              }
            }
            .red-text {
              font-size: 16px;
              line-height: 18px;
              @include responsive {
                font-size: 12px;
                line-height: 12px;
              }
            }
          }
          .middle-right {
            flex-basis: 40%;
            img {
              max-width: 127.1px;
              max-height: 90.7px;
              border-radius: 4px;
              @include responsive {
                max-width: 107.1px;
                max-height: 76px;
                border-radius: 8px;
              }
            }
          }
        }
        .subscribe-upgrade-bottom {
          padding: 2px;
        }
      }
    }

    &.variant-plp-list {
      @include responsive {
        flex: 1 1 calc(100% - 8px);
      }
      .save-percentage-web {
        @include responsive {
          display: block;
        }
      }
      .save-percentage-mweb {
        @include responsive {
          display: none;
        }
      }

      &.user-NOPRIME,
      &.user-VIPPRIME.expired-VIP,
      &.user-BASICPRIME.expired-BASIC {
        .middle {
          width: 100%;
          // padding: 16px 8px;
          .middle-left {
            flex-basis: 65%;
            @include responsive {
              flex-basis: 70%;
            }
            h4 {
              font-size: 12px;
              line-height: 14px;

              @include responsive {
                font-size: 14px;
              }
            }
            .price {
              font-size: 20px;
              line-height: 26px;
              margin-top: 2px;
            }
            .vat {
              font-size: 8px;
              @include responsive {
                font-size: 9px;
              }
            }
            .savings {
              .striked-off {
                @include responsive {
                  font-size: 10px;
                  line-height: 12px;
                  padding-left: 4px;
                  border-left: 0.4px solid $color-gray-border;
                }
              }
              .save-percent-tag {
                @include responsive {
                  font-size: 10px;
                  line-height: 18px;
                  padding: 2px 4px;
                }
              }
            }
          }
          .middle-right {
            flex-basis: 35%;
            @include responsive {
              flex-basis: 30%;
            }
            img {
              border-radius: 6px;
              max-width: 59px;
              max-height: 42px;
              @include responsive {
                max-width: 78px;
                max-height: 56px;
                border-radius: 8px;
              }
            }
          }
        }
        .subscribe-upgrade-bottom {
          @include responsive {
            padding: 2px;
          }
          .subscribe-upgrade-link {
            font-size: 9px;
            @include responsive {
              font-size: 10px;
            }
          }
        }
      }

      &.user-VIPPRIME,
      &.user-BASICPRIME {
        .middle {
          // padding: 16px 8px;
          .middle-left {
            flex-basis: 70%;
            h4 {
              font-size: 18px;
              @include responsive {
                font-size: 12px;
              }
            }
            .price {
              margin-top: 4px;
              font-size: 22px;
              line-height: 26px;
              @include responsive {
                font-size: 20px;
                line-height: 24px;
                margin-top: 0px;
              }
            }
            .vat {
              @include responsive {
                font-size: 10px;
                line-height: 12px;
              }
            }
            .striked-off {
              font-size: 16px;
              line-height: 18px;
              @include responsive {
                font-size: 12px;
                line-height: 12px;
              }
            }
            .red-text {
              font-size: 16px;
              line-height: 18px;
              @include responsive {
                font-size: 12px;
                line-height: 12px;
              }
            }
          }
          .middle-right {
            flex-basis: 30%;
            @include responsive {
              flex-basis: 40%;
            }
            img {
              max-width: 96px;
              max-height: 68px;
              border-radius: 10px;
              @include responsive {
                max-width: 44px;
                max-height: 35px;
                border-radius: 2px;
              }
            }
          }
        }
      }
      &.user-NOPRIME {
        .middle .middle-left {
          align-items: center;
          flex-direction: row;
          justify-content: flex-start;
          gap: 8px;
        }
      }
    }

    &.megasale-theme {
      .save-percent-tag {
        background-color: $extraTagBg;
        color: $extraTagTextColor;
      }
      .orange-text {
        color: $extraTagBg;
      }
    }
    &:nth-of-type(3) {
      flex-basis: 100%;
    }
    &.discount-applied-section.rtl {
      section.price {
        display: flex;
        align-items: baseline;
        gap: 4px;
        .vat {
          width: 115px;
        }
      }
    }

    &.variant-restock-popup {
      h4 {
        font-weight: 400;
      }
      .price {
        font-size: 24px;
        line-height: 26px;
      }
      .save-percent-tag {
        display: none;
      }
      .subscribe-upgrade-link {
        font-size: 13px;
      }
      .striked-off,
      .orange-text {
        font-size: 14px;
        line-height: 16px;
      }
    }

    &.rtl {
      .save-percent-tag.top-right-tag {
        right: 10px;
      }
      .price .vat {
        font-size: 8px;
      }
    }
    &.variant-plp-grid {
      .middle .middle-right {
        img {
          border-radius: 3px;

          @include responsive {
            max-width: 30px;
            border-radius: 2px;
          }
        }
      }
      .save-percentage-mweb {
        @include responsive {
          display: block;
          margin-top: 0;
        }
      }
      .save-percentage-web {
        @include responsive {
          display: none;
        }
      }
    }
    @include responsive {
      gap: 6px;
    }
  }
  :global(.jood-addtocart-popup .product-price-new-container .subscribe-upgrade-bottom .subscribe-upgrade-link) {
    font-size: 12px !important;
  }
  :global(.jood-addtocart-popup .product-price-new-container .middle .middle-left h4) {
    font-size: 18px !important;
    @include responsive {
      font-size: 14px !important;
    }
  }
  :global(.jood-addtocart-popup .product-price-new-container .middle .middle-left .price) {
    font-size: 24px !important;
    line-height: 30px !important;
  }
  :global(.jood-addtocart-popup .product-price-new-container .middle .middle-left .vat) {
    font-size: 12px !important;
  }

  @include responsive {
    :global {
      .right-container .c_expirecard.price-expire-card .expired-text-container {
        flex-direction: row;
        gap: 16px;
      }
    }
  }
  :global(.variant-pdp .jood-saving-section.product-card .jood-saving-container .price) {
    font-size: 22px !important;
    @include responsive {
      font-size: 14px !important;
      line-height: 20px;
    }
  }
  :global {
    html.bh.rtl {
      .product-tile-card-container {
        .price-container {
          .product-tile-price-new-group.variant-plp-grid {
            .product-price-new-container.discount-applied-section.rtl {
              section.price {
                display: block;
              }
              .save-percent-tag.top-right-tag {
                left: 0;
                top: 27px;
              }
            }
          }
        }
      }
    }
  }
</style>
